import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Phone, UserCheck, PoundSterling, AlertTriangle, Users } from "lucide-react";
import { api } from "@/lib/api";
import { STAGES, stageMeta, gbp } from "@/lib/constants";
import { StatCard } from "@/components/StatCard";
import { TeamAvatar } from "@/components/TeamAvatar";
import { FollowUpBadge } from "@/components/FollowUpBadge";

const MiniStat = ({ label, value, sub, testid }) => (
  <div className="rounded-xl bg-slate-50 p-4" data-testid={testid}>
    <p className="font-display text-2xl font-bold text-slate-900">{value}</p>
    <p className="text-xs font-medium text-slate-600">{label}</p>
    {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [team, setTeam] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.dashboard().then(setStats);
    api.followupsToday().then(setFollowups);
    api.team().then(setTeam);
    api.analyticsOutreach().then(setAnalytics);
  }, []);

  const memberById = (id) => team.find((m) => m.id === id);
  const total = stats?.total_enquiries || 1;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">A snapshot of your enquiry pipeline and onboarding activity.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-new-enquiries" label="New Enquiries" value={stats?.new_enquiries ?? "—"} sub="Awaiting first contact" icon={Inbox} accent="#0ea5e9" delay={0} />
        <StatCard testid="stat-intro-calls" label="Intro Calls This Week" value={stats?.intro_calls_week ?? "—"} sub="Scheduled Mon–Sun" icon={Phone} accent="#8b5cf6" delay={60} />
        <StatCard testid="stat-onboarding" label="Clients Onboarding" value={stats?.onboarding ?? "—"} sub="In onboarding stage" icon={UserCheck} accent="#059669" delay={120} />
        <StatCard testid="stat-pipeline-value" label="Pipeline Value" value={stats ? gbp(stats.pipeline_value) : "—"} sub="Open opportunities" icon={PoundSterling} accent="#0f172a" delay={180} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">Pipeline overview</h2>
            <button onClick={() => navigate("/pipeline")} className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="view-pipeline-link">View board →</button>
          </div>
          <div className="mt-5 space-y-4">
            {STAGES.map((s) => {
              const count = stats?.stage_counts?.[s.key] || 0;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={s.key} data-testid={`pipeline-row-${s.key}`}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 font-medium text-slate-700">
                      <span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}
                    </span>
                    <span className="font-mono-fin text-slate-500">{count}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${s.dot} transition-all duration-700`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-5">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 text-rose-600"><AlertTriangle className="h-5 w-5" /></div>
              <div><p className="font-display text-xl font-bold text-slate-900">{stats?.overdue_followups ?? 0}</p><p className="text-xs text-slate-500">Overdue follow-ups</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-teal-50 text-teal-600"><Users className="h-5 w-5" /></div>
              <div><p className="font-display text-xl font-bold text-slate-900">{stats?.active_clients ?? 0}</p><p className="text-xs text-slate-500">Active client records</p></div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">Today's follow-ups</h2>
            <button onClick={() => navigate("/today")} className="text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="view-today-link">All →</button>
          </div>
          <div className="mt-4 space-y-3">
            {followups.length === 0 && <p className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500" data-testid="followups-empty">All caught up! No pending follow-ups.</p>}
            {followups.slice(0, 6).map((e) => {
              const sm = stageMeta(e.stage);
              return (
                <button key={e.id} onClick={() => navigate("/enquiries")} data-testid={`followup-item-${e.id}`} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-100 p-3 text-left transition-colors hover:border-slate-200 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{e.business_name}</p>
                    <p className="truncate text-xs text-slate-500">{e.contact_name} · <span className={`rounded px-1 ${sm.badge}`}>{sm.label}</span></p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <FollowUpBadge date={e.follow_up_date} />
                    <TeamAvatar member={memberById(e.assignee_id)} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {analytics && (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="outreach-analytics">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">Outreach performance</h2>
            <span className="text-xs text-slate-400">{analytics.sent} emails sent{analytics.followups_sent ? ` · ${analytics.followups_sent} follow-ups` : ""}</span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <MiniStat label="Emails sent" value={analytics.sent} testid="oa-sent" />
            <MiniStat label="Open rate" value={`${analytics.open_rate}%`} sub={`${analytics.opened} opened`} testid="oa-open" />
            <MiniStat label="Reply rate" value={`${analytics.reply_rate}%`} sub={`${analytics.replied} replied`} testid="oa-reply" />
            <MiniStat label="Opted out" value={analytics.opted_out} testid="oa-opt" />
          </div>
          {analytics.by_sector.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Response by sector</p>
              <div className="space-y-3">
                {analytics.by_sector.map((s) => (
                  <div key={s.sector} data-testid={`oa-sector-${s.sector}`} className="flex items-center gap-3">
                    <span className="w-40 shrink-0 truncate text-sm text-slate-700">{s.sector}</span>
                    <div className="flex-1">
                      <div className="mb-1 flex justify-between text-[11px] text-slate-500"><span>{s.sent} sent</span><span>{s.reply_rate}% reply · {s.open_rate}% open</span></div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-emerald-500 transition-all duration-700" style={{ width: `${s.reply_rate}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="mt-4 text-[11px] text-slate-400">Open tracking uses a pixel and is indicative only — some email apps pre-load or block images.</p>
        </section>
      )}
    </div>
  );
}
